import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateClimberFields } from '../services/firebase/climber';
import { getGym } from '../services/firebase/gym';
import PageHeader from '../components/layout/PageHeader';
import CloseButton from '../components/CloseButton';
import '../styles/JoinGym.css';

// Hard-coded gym codes (not secure, but sufficient for current needs)
const GYM_CODES: { [code: string]: string } = {
  '123451': 'gym_1742122234102_f0prcti',
  '123452': 'gym_1742122635722_luyq1z4',
  '123453': 'gym_1753542341353_92g53x2'
};

const JoinGym: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, climberProfile, refreshClimberProfile } = useAuth();
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCodeChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setCode(numericValue);
    
    // Clear any previous messages when typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Clear any previous messages when typing
    if (error) setError('');
    if (success) setSuccess('');

    if (e.key === 'Backspace') {
      e.preventDefault();
      // Remove the last digit
      setCode(prev => prev.slice(0, -1));
    } else if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      // Add digit from left to right, max 6 digits
      if (code.length < 6) {
        setCode(prev => prev + e.key);
      }
    }
    // Ignore all other keys
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser || !climberProfile) {
      setError('Please log in to join a gym');
      return;
    }

    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    const gymId = GYM_CODES[code];
    if (!gymId) {
      setError('Invalid gym code. Please check the code and try again.');
      return;
    }

    // Check if user is already a member of this gym
    if (climberProfile.isMemberOfGym(gymId)) {
      setError('You are already a member of this gym');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify the gym exists
      const gym = await getGym(gymId);
      if (!gym) {
        setError('Gym not found. Please contact support.');
        return;
      }

      // Add user to gym with 'climber' role
      climberProfile.updateGymMembership(gymId, 'climber');
      
      // Update the user document in Firestore
      await updateClimberFields(currentUser.uid, { 
        gymMemberships: climberProfile.gymMemberships 
      });

      // Refresh the climber profile in context
      await refreshClimberProfile();

      setSuccess(`Successfully joined ${gym.name}!`);
      
      // Redirect to the gym detail page after a short delay
      setTimeout(() => {
        navigate(`/gyms/${gymId}`);
      }, 2000);

    } catch (err) {
      console.error('Error joining gym:', err);
      setError('Failed to join gym. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInputClick = () => {
    // Focus the input when clicking on the code display
    const input = document.getElementById('gym-code-input') as HTMLInputElement;
    if (input) {
      input.focus();
      // On mobile, sometimes we need to trigger the keyboard manually
      if ('ontouchstart' in window) {
        input.click();
      }
    }
  };

  return (
    <div className="join-gym-container">
      <PageHeader title="Join Gym" showMenu={false} />
      <CloseButton onClose={() => navigate(-1)} />
      
      <div className="join-gym-content">
        <div className="join-gym-card">
          <div className="join-gym-header">
            <h2>Enter Gym Code</h2>
            <p>Enter the 6-digit code provided by your gym to join</p>
          </div>

          <form onSubmit={handleSubmit} className="join-gym-form">
            <div className="code-input-section">
              <label htmlFor="gym-code-input" className="code-label">
                Gym Code
              </label>
              <div className="code-display" onClick={handleCodeInputClick}>
                {Array.from({ length: 6 }, (_, index) => (
                  <div 
                    key={index} 
                    className={`code-digit ${index < code.length ? 'filled' : ''} ${index === code.length ? 'active' : ''}`}
                  >
                    {code[index] || ''}
                  </div>
                ))}
              </div>
              <input
                id="gym-code-input"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onKeyDown={handleKeyDown}
                onInput={(e) => {
                  // Handle mobile input events
                  const target = e.target as HTMLInputElement;
                  const value = target.value;
                  
                  // Clear any previous messages
                  if (error) setError('');
                  if (success) setSuccess('');
                  
                  // Only allow numbers and limit to 6 digits
                  const numericValue = value.replace(/\D/g, '').slice(0, 6);
                  setCode(numericValue);
                  
                  // Keep cursor at end
                  setTimeout(() => {
                    target.setSelectionRange(numericValue.length, numericValue.length);
                  }, 0);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  handleCodeChange(pastedText);
                }}
                onChange={() => {
                  // Prevent default onChange behavior since we handle input via onInput and keyDown
                }}
                className="code-input-hidden"
                placeholder="Enter 6-digit code"
                autoComplete="off"
                autoFocus
                maxLength={6}
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            <button 
              type="submit" 
              className="join-gym-button"
              disabled={loading || code.length !== 6 || success !== ''}
            >
              {loading ? 'Joining...' : 'Join Gym'}
            </button>
          </form>

          <div className="join-gym-info">
            <h3>Don't have a code?</h3>
            <p>
              Contact your gym's staff or check your membership materials 
              for the 6-digit gym code. This code is unique to each gym 
              and allows you to access their routes and participate in 
              their climbing community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinGym;
